import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Modal, Progress, Space, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClientFormValues } from '../domain/idCardClientForm';
import { isSensitiveIdentityField, maskIdentityValue } from '../domain/identityMasking';
import {
    applyOpticalAddress,
    addressAuditOutcome,
    reviewOpticalAddress,
    type OpticalAddressReviewField,
    type OpticalAddressReviewItem,
} from '../domain/opticalAddressProposal';
import {
    applyOpticalConflicts,
    proposeOpticalClientForm,
    type OpticalFieldProposal,
    type OpticalFormField,
} from '../domain/opticalClientForm';
import {
    classifyUnclaimedScan,
    opticalProvenance,
    type ScanProvenance,
} from '../domain/documentScanAudit';
import {
    describeScanWarnings,
    noticesOfSeverity,
    severitiesPresent,
} from '../domain/documentScanWarnings';
import { useDocumentScan } from '../hooks/useDocumentScan';
import type { OpticalScanSession } from '../domain/opticalScan';
import { DocumentAddressBoundary } from './DocumentAddressBoundary';
import { DocumentAddressSection } from './DocumentAddressSection';

/**
 * Reads a document optically and shows what it read before anything reaches the form.
 *
 * The review step is not a courtesy. An optical scan is a photograph of a machine-readable zone: it
 * can be misread, the document can be expired, and the check digits can disagree with the values
 * they cover. Everything here is shown to a person, with the disagreements called out, and nothing
 * is applied until they say so.
 *
 * Nothing read here is logged, stored or sent anywhere. It lives in this component until the modal
 * closes, at which point the workstation service is told to forget it too.
 */
export interface DocumentScanModalProps {
    visible: boolean;
    /** The reader the launcher resolved. A scan always names its device. */
    deviceId: string | null;
    /** What the form holds now, so the review can show what would change. */
    currentValues: Partial<ClientFormValues>;
    onClose: () => void;
    onApply: (
        values: Partial<ClientFormValues>,
        /**
         * The portrait's bytes, or null when the document produced none.
         *
         * A `Blob`, not base64: the encoding the upload route needs happens at the upload, and the
         * encoded copy is released there rather than living in this page's state. The visible and
         * infrared images are deliberately not passed - they have nowhere central to go.
         */
        portrait: Blob | null,
        provenance: ScanProvenance | null,
    ) => void;
    /** The workstation this scan is running on, for the audit the save will write. */
    installationId?: string | null;
    /**
     * Called once when a scan ends without its values reaching a client record.
     *
     * A read that failed, was abandoned, or produced values the operator then walked away from
     * leaves no other central trace: the scan happens between this browser and the workstation
     * service, and the backend is deliberately not on that path. A save records its own event; this
     * is the only way the other endings are ever recorded.
     *
     * Fires at most once per scan, and never for a scan whose values were applied - that ending is
     * recorded by the save itself, where it can be truthful about whether anything was stored.
     */
    onTerminalOutcome?: (
        eventType: 'scan_completed' | 'scan_failed' | 'scan_cancelled',
        provenance: ScanProvenance | null,
        outcomeCode: string | null,
    ) => void;
}

/** How far through a scan each state is, for the progress bar. */
const PROGRESS: Record<string, number> = {
    ready: 5,
    'waiting-for-document': 20,
    'document-detected': 45,
    reading: 70,
    decoding: 90,
    completed: 100,
};

/**
 * The list with `field` added, and the list with `field` removed.
 *
 * Two functions rather than one with a flag: the row already knows which of the two it wants when
 * its checkbox changes, so it calls that one. Kept out of the component so the per-row handlers
 * stay shallow - the row callback, the state updater and the list change are three separate steps.
 */
function withField<Field>(current: readonly Field[], field: Field): Field[] {
    return [...current, field];
}

function withoutField<Field>(current: readonly Field[], field: Field): Field[] {
    return current.filter(existing => existing !== field);
}

export const DocumentScanModal = ({
    visible,
    deviceId,
    currentValues,
    onClose,
    onApply,
    installationId,
    onTerminalOutcome,
}: DocumentScanModalProps) => {
    const { t } = useTranslation();

    const [accepted, setAccepted] = useState<OpticalFormField[]>([]);
    // Address components are ticked separately and start empty every time. There is deliberately
    // no code path that seeds this from a confidence score or from the proposal itself.
    const [acceptedAddress, setAcceptedAddress] = useState<OpticalAddressReviewField[]>([]);
    const [revealed, setRevealed] = useState(false);

    const accept = useCallback((field: OpticalFormField) => {
        setAccepted(current => withField(current, field));
    }, []);
    const reject = useCallback((field: OpticalFormField) => {
        setAccepted(current => withoutField(current, field));
    }, []);
    const acceptAddress = useCallback((field: OpticalAddressReviewField) => {
        setAcceptedAddress(current => withField(current, field));
    }, []);
    const rejectAddress = useCallback((field: OpticalAddressReviewField) => {
        setAcceptedAddress(current => withoutField(current, field));
    }, []);
    /** The scan whose ending has already been reported, so it is reported once and not per render. */
    const reported = useRef<string | null>(null);
    const applied = useRef(false);
    /**
     * The installation, held so the terminal reporter below does not have to be rebuilt for it.
     *
     * The reporter is handed to `useDocumentScan`, and a hook that rebuilt its callbacks whenever
     * this component re-rendered would restart the scan.
     */
    const installation = useRef(installationId ?? null);
    const report = useRef(onTerminalOutcome);

    useEffect(() => {
        installation.current = installationId ?? null;
        report.current = onTerminalOutcome;
    });

    /**
     * Records the read itself, at the moment the scan ends and before anything renders.
     *
     * **This used to be an effect, and that is why two real reads left no trace.** A completed read
     * was reported by a `useEffect` in this component, which React runs only after a render has
     * committed - so on 2026-09-14, when the render threw, the effect never ran and the central
     * audit row was never written for either scan. The evidence that a document had been read
     * centrally depended on the screen being able to draw it.
     *
     * It is now called by the hook the instant it accepts a terminal response, before a single
     * piece of state moves. A rendering defect cannot suppress it, because nothing has been asked
     * to render yet.
     *
     * Only the read is recorded here. What the operator does next is a different fact: applying
     * values and saving a client is recorded by the save, with its own `data_applied`.
     */
    const handleTerminal = useCallback((finished: OpticalScanSession) => {
        const write = report.current;

        if (write === undefined || finished.result === null) {
            return;
        }

        if (reported.current === finished.sessionId) {
            return;
        }

        reported.current = finished.sessionId;

        // Built from the session, which carries opaque handles, timings and codes. Nothing the
        // document said travels with it - see `domain/documentScanAudit.ts`. The ref is a
        // convenience, not the guarantee: the backend's unique key on (session, event type) is the
        // authority, because a ref cannot survive a remount, a second tab or a retried request.
        write(
            'scan_completed',
            opticalProvenance(finished, installation.current, addressAuditOutcome(finished.result)),
            'OK',
        );
    }, []);

    const { phase, session, result, error, failure, previews, portrait, start, cancel, reset } =
        useDocumentScan({ onTerminal: handleTerminal });

    useEffect(() => {
        if (!visible) {
            return;
        }

        setAccepted([]);
        setRevealed(false);
        reported.current = null;
        applied.current = false;
        void start(deviceId);
    }, [visible, deviceId, start]);

    const proposal = useMemo(
        () => (result === null ? null : proposeOpticalClientForm(result, currentValues)),
        [result, currentValues],
    );

    /**
     * Every code the service returned, as something an operator can act on.
     *
     * The codes were always in the response and were never rendered. That is why the 2026-09-03
     * acceptance operator watched three fields come back empty with no explanation on the screen:
     * the reason was in the payload the whole time, under `warnings`.
     */
    const notices = useMemo(() => describeScanWarnings(result?.warnings), [result]);

    /**
     * Reports an ending that produced nothing, exactly once.
     *
     * Keyed on the session id rather than on a boolean, so a second scan in the same open modal is
     * reported on its own account; a scan that never got a session (the service never answered) is
     * still reported, under a sentinel, because "the workstation could not reach its service" is a
     * fact worth having.
     */
    useEffect(() => {
        if (onTerminalOutcome === undefined || phase !== 'finished' || applied.current) {
            return;
        }

        const failed = error !== null || (failure !== null && result === null);
        const cancelled = session?.state === 'cancelled';

        if (!failed && !cancelled) {
            return;
        }

        const key = session?.sessionId ?? 'no-session';

        if (reported.current === key) {
            return;
        }

        reported.current = key;

        onTerminalOutcome(
            cancelled ? 'scan_cancelled' : 'scan_failed',
            session === null
                ? null
                : opticalProvenance(session, installationId ?? null, addressAuditOutcome(result)),
            error ?? failure,
        );
    }, [phase, error, failure, result, session, installationId, onTerminalOutcome]);

    /**
     * Records a scan the operator walked away from, whatever it had become.
     *
     * The endings nothing else writes: a save records `data_applied`, and the effect above records
     * failures and cancellations *at the moment they happen*. What is left is every way a session
     * stops being this modal's business without being claimed - the operator closes, or retries,
     * or abandons a scan that was still waiting for a document.
     *
     * It belongs here rather than in that effect because a completed scan only becomes a
     * *discarded* one when somebody leaves without taking the values; reporting it when the values
     * appeared would file the row before the decision it describes had been made.
     *
     * Shares `reported` with the effect, so one scan produces one row whichever way it ended.
     */
    const reportUnclaimedScan = () => {
        if (onTerminalOutcome === undefined || applied.current || session === null) {
            return;
        }

        const key = session.sessionId;

        if (reported.current === key) {
            return;
        }

        reported.current = key;

        const ending = classifyUnclaimedScan({
            hasResult: result !== null,
            error,
            failure,
            cancelled: session.state === 'cancelled',
            finished: phase === 'finished',
        });

        onTerminalOutcome(
            ending.eventType,
            opticalProvenance(session, installationId ?? null, addressAuditOutcome(result)),
            ending.outcomeCode,
        );
    };

    const close = () => {
        reportUnclaimedScan();

        // Every way out of this modal disposes of the scan: the service otherwise holds the document
        // for five minutes after the operator has finished with it.
        void reset();
        onClose();
    };

    /**
     * Starts again, after accounting for the scan being replaced.
     *
     * Retry is the ordinary way an operator reaches a second scan, and it is not a way of undoing
     * the first: that session read a real document, and the row saying so is owed whether or not
     * anybody kept the values. The hook disposes of the superseded session; this records it.
     */
    const retry = () => {
        reportUnclaimedScan();
        void start(deviceId);
    };

    const addressReview = useMemo(
        () => reviewOpticalAddress(result, currentValues),
        [result, currentValues],
    );

    const apply = () => {
        if (proposal === null) {
            return;
        }

        // Built from the session, which carries opaque handles and timings. Nothing the
        // document said travels with it - see `domain/documentScanAudit.ts`.
        // The save records this ending, so the terminal reporter must not also record it.
        applied.current = true;

        onApply(
            // The zone's fields first, then whichever address components a person ticked. Address
            // never joins the automatic half: `applyOpticalAddress` returns nothing for an empty
            // selection, which is exactly what an operator who accepted none of it produces.
            {
                ...applyOpticalConflicts(proposal, accepted),
                ...applyOpticalAddress(addressReview, acceptedAddress),
            },
            portrait,
            session === null
                ? null
                : opticalProvenance(session, installationId ?? null, addressAuditOutcome(result)),
        );
        void reset();
        onClose();
    };

    const running = phase === 'starting' || phase === 'scanning';
    const state = session?.state ?? 'ready';
    const outcomeShown = error !== null || (failure !== null && result === null);

    return (
        <Modal
            title={t('documentScan.title')}
            open={visible}
            onCancel={close}
            width={720}
            destroyOnHidden
            footer={[
                running ? (
                    <Button key="cancel" onClick={() => void cancel()}>
                        {t('documentScan.cancel')}
                    </Button>
                ) : (
                    <Button key="retry" onClick={retry}>
                        {t('documentScan.retry')}
                    </Button>
                ),
                <Button key="close" onClick={close}>
                    {t('documentScan.close')}
                </Button>,
                <Button key="apply" type="primary" disabled={proposal === null} onClick={apply}>
                    {t('documentScan.apply')}
                </Button>,
            ]}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Announced politely rather than assertively: a scan changes state several times a
                    second at the end, and interrupting a screen reader that often is unusable. */}
                <output aria-live="polite" style={{ display: 'block' }}>
                    {/* Suppressed once an alert below is carrying the outcome: the state line and
                        the failure sentence say the same thing, and two copies of it read as two
                        separate problems. */}
                    <Typography.Text>
                        {outcomeShown ? '' : t(`documentScan.state.${state}`)}
                    </Typography.Text>
                </output>

                {running && (
                    <Progress
                        percent={PROGRESS[state] ?? 10}
                        status="active"
                        aria-label={t('documentScan.progressLabel')}
                    />
                )}

                {error !== null && (
                    <Alert type="error" showIcon message={t(`documentScan.error.${error}`)} />
                )}

                {failure !== null && result === null && (
                    <Alert type="warning" showIcon message={t(`documentScan.failure.${failure}`)} />
                )}

                {proposal !== null && !proposal.trustworthy && (
                    <Alert
                        type="warning"
                        showIcon
                        message={t('documentScan.validationWarning')}
                        description={t('documentScan.validationWarningDetail')}
                    />
                )}

                {/* One alert per severity rather than one per code: six stacked alerts is a wall
                    an operator scrolls past, and the grouping is exactly the decision they have to
                    make - which of these must I check, and which is just how the document works. */}
                {severitiesPresent(notices).map(severity => (
                    <Alert
                        key={severity}
                        type={severity}
                        showIcon
                        message={t(
                            `documentScan.warnings${severity[0].toUpperCase()}${severity.slice(1)}`,
                        )}
                        description={
                            <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
                                {noticesOfSeverity(notices, severity).map(notice => (
                                    <li
                                        key={notice.code}
                                        data-testid={`scan-warning-${notice.code}`}
                                    >
                                        {t(notice.translationKey, { code: notice.code })}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                ))}

                {result?.documentExpired === true && (
                    <Alert type="warning" showIcon message={t('documentScan.expired')} />
                )}

                {proposal !== null && (
                    <>
                        <Space align="center">
                            <Typography.Title level={5} style={{ margin: 0 }}>
                                {t('documentScan.reviewTitle')}
                            </Typography.Title>
                            <Button
                                size="small"
                                icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                onClick={() => setRevealed(current => !current)}
                            >
                                {t(revealed ? 'documentScan.hide' : 'documentScan.reveal')}
                            </Button>
                        </Space>

                        <dl>
                            {proposal.proposals.map(entry => (
                                <ReviewRow
                                    key={entry.field}
                                    entry={entry}
                                    revealed={revealed}
                                    accepted={accepted.includes(entry.field)}
                                    onAccept={() => accept(entry.field)}
                                    onReject={() => reject(entry.field)}
                                />
                            ))}
                        </dl>

                        {/* Always rendered on a completed scan, in whatever state the reader left
                            the address. A section that hides itself when it has nothing to offer
                            cannot tell an operator that calibration is the thing standing in the
                            way - which is exactly what happened on 2026-09-11. */}
                        {/* Bounded, because on 2026-09-14 a TypeError thrown inside this section
                            unmounted the whole React tree and an operator watched the application
                            turn white with a card on the reader. The identity fields above and the
                            controls below stay on screen: the address is the one part of a result
                            they can always type by hand, so it is the only part worth isolating. */}
                        <DocumentAddressBoundary>
                            <DocumentAddressSection
                                review={addressReview}
                                previews={{
                                    visible:
                                        previews.find(preview => preview.kind === 'visible')?.url ??
                                        null,
                                    infrared:
                                        previews.find(preview => preview.kind === 'infrared')
                                            ?.url ?? null,
                                }}
                            >
                                <dl style={{ margin: 0 }}>
                                    {addressReview.items.map(item => (
                                        <AddressReviewRow
                                            key={item.field}
                                            item={item}
                                            revealed={revealed}
                                            accepted={acceptedAddress.includes(item.field)}
                                            onAccept={() => acceptAddress(item.field)}
                                            onReject={() => rejectAddress(item.field)}
                                        />
                                    ))}
                                </dl>
                            </DocumentAddressSection>
                        </DocumentAddressBoundary>

                        {previews.length > 0 && (
                            <Space size="middle" wrap align="start">
                                {/* Only the kinds this scan produced. A reader that returned no
                                    infrared image shows no infrared panel, rather than an empty
                                    frame implying one was taken and lost. */}
                                {previews.map(preview => (
                                    <figure
                                        key={preview.kind}
                                        style={{ margin: 0 }}
                                        data-testid={`scan-preview-${preview.kind}`}
                                    >
                                        <img
                                            src={preview.url}
                                            alt={t(`documentScan.preview.${preview.kind}Alt`)}
                                            style={{ maxHeight: 160, display: 'block' }}
                                        />
                                        <figcaption>
                                            <Typography.Text type="secondary">
                                                {t(`documentScan.preview.${preview.kind}`)}
                                            </Typography.Text>
                                        </figcaption>
                                    </figure>
                                ))}
                            </Space>
                        )}
                    </>
                )}
            </Space>
        </Modal>
    );
};

/** One reviewed field: what the scan read, what the form has, and what will happen. */
/**
 * One proposed address component.
 *
 * Three things every row states, because an optical address is not a machine-readable-zone field
 * and must not look like one: where it came from, that its reliability is unknown, and that it is
 * unticked. The checkbox is the only way a value moves, and nothing renders it pre-ticked.
 */
const AddressReviewRow = ({
    item,
    revealed,
    accepted,
    onAccept,
    onReject,
}: {
    item: OpticalAddressReviewItem;
    revealed: boolean;
    accepted: boolean;
    /** The checkbox was ticked: use this value. */
    onAccept: () => void;
    /** The checkbox was cleared: leave the form's own value alone. */
    onReject: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <div style={{ marginBottom: 12 }} data-testid={`address-row-${item.field}`}>
            <dt>
                <Space size={6} wrap>
                    <Typography.Text strong>
                        {t(`documentScan.addressField.${item.field}`)}
                    </Typography.Text>
                    {/* A composed row says so where the source tag would be: it was not read as
                        one value by anything, and must not wear the tag that says it was. */}
                    {item.role === 'derived' ? (
                        <Tag color="purple">{t('documentScan.addressDerived')}</Tag>
                    ) : (
                        <Tag color="blue">{t('documentScan.sourceOpticalOcr')}</Tag>
                    )}
                    <Tag color="warning">{t('documentScan.confidenceUnavailable')}</Tag>
                    {item.role === 'source' && (
                        <Tag>{t('documentScan.addressSourceComponent')}</Tag>
                    )}
                    {!item.storable && <Tag>{t('documentScan.addressNotStorable')}</Tag>}
                    {item.blocked !== null && (
                        <Tag color="error">{t(`documentScan.addressBlocked.${item.blocked}`)}</Tag>
                    )}
                    {item.conflict && <Tag color="error">{t('documentScan.conflict')}</Tag>}
                </Space>
            </dt>
            <dd style={{ margin: '4px 0 0 0' }}>
                <Typography.Text code>
                    {revealed ? item.proposed : maskIdentityValue(item.proposed)}
                </Typography.Text>

                {item.conflict && (
                    <div>
                        <Typography.Text type="secondary">
                            {t('documentScan.addressCurrentValue', {
                                value: revealed ? item.current : maskIdentityValue(item.current),
                            })}
                        </Typography.Text>
                    </div>
                )}

                {item.role === 'derived' && (
                    <div>
                        <Typography.Text type="secondary">
                            {t('documentScan.addressDerivedDetail')}
                        </Typography.Text>
                    </div>
                )}

                {item.blocked !== null && (
                    <div>
                        <Typography.Text type="secondary">
                            {t(`documentScan.addressBlockedDetail.${item.blocked}`)}
                        </Typography.Text>
                    </div>
                )}

                {item.storable ? (
                    <div>
                        {/* Disabled, not hidden, when blocked: the operator sees that the value
                            exists and that it cannot be chosen, rather than a row with no way in. */}
                        <Checkbox
                            checked={accepted}
                            disabled={!item.applicable}
                            onChange={event => (event.target.checked ? onAccept() : onReject())}
                        >
                            {t('documentScan.addressAccept')}
                        </Checkbox>
                    </div>
                ) : (
                    <div>
                        <Typography.Text type="secondary">
                            {t(
                                item.role === 'source'
                                    ? 'documentScan.addressSourceDetail'
                                    : 'documentScan.addressNotStorableDetail',
                            )}
                        </Typography.Text>
                    </div>
                )}
            </dd>
        </div>
    );
};

const ReviewRow = ({
    entry,
    revealed,
    accepted,
    onAccept,
    onReject,
}: {
    entry: OpticalFieldProposal;
    revealed: boolean;
    accepted: boolean;
    /** The checkbox was ticked: use this value. */
    onAccept: () => void;
    /** The checkbox was cleared: leave the form's own value alone. */
    onReject: () => void;
}) => {
    const { t } = useTranslation();
    const label = t(`documentScan.field.${entry.field}`);
    const show = (value: string) =>
        revealed || !isSensitiveIdentityField(entry.field) ? value : maskIdentityValue(value);

    return (
        <>
            <dt>
                <Typography.Text strong>{label}</Typography.Text>{' '}
                <Tag>{t(`documentScan.outcome.${entry.outcome}`)}</Tag>
            </dt>
            <dd>
                {entry.outcome === 'unavailable' ? (
                    <Typography.Text type="secondary">{t('documentScan.notRead')}</Typography.Text>
                ) : (
                    <Typography.Text>{show(entry.scanned)}</Typography.Text>
                )}

                {entry.outcome === 'conflict' && (
                    <div>
                        <Typography.Text type="secondary">
                            {t('documentScan.currentValue', { value: show(entry.current) })}
                        </Typography.Text>
                        <div>
                            {/* Unchecked by default: overwriting what somebody typed is an act with
                                a decision behind it, never a side effect of scanning. */}
                            <Checkbox
                                checked={accepted}
                                onChange={event => (event.target.checked ? onAccept() : onReject())}
                            >
                                {t('documentScan.useScanned', { field: label })}
                            </Checkbox>
                        </div>
                    </div>
                )}

                {entry.outcome === 'rejected' && (
                    <Typography.Text type="danger"> {t('documentScan.rejected')}</Typography.Text>
                )}
            </dd>
        </>
    );
};

export default DocumentScanModal;
